import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errDetail } from "@/lib/api";
import { cacheGet, cacheSet } from "@/lib/swcache";
import { useAuth } from "@/App";
import { Sidebar } from "@/components/Sidebar";
import { BillingBanner } from "@/components/BillingBanner";
import { EmailList } from "@/components/EmailList";
import { ThreadView } from "@/components/ThreadView";
import { AgentPanel } from "@/components/AgentPanel";
import { AuditPanel } from "@/components/AuditPanel";
import { GlobalSearch } from "@/components/GlobalSearch";
import { t as t2 } from "@/lib/i18n";

export default function InboxPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [folder, setFolder] = useState("inbox");
  const [mailbox, setMailbox] = useState(() => new URLSearchParams(window.location.search).get("mailbox") || "");
  const [mailboxName, setMailboxName] = useState("");
  const [showAudit, setShowAudit] = useState(false);
  useEffect(() => {
    if (mailbox && !mailboxName) {
      api.get("/shared-inboxes").then((r) => {
        const si = (r.data.shared_inboxes || []).find((s) => s.id === mailbox);
        if (si) setMailboxName(si.name);
      }).catch(() => {});
    }
  }, [mailbox, mailboxName]);
  const [folderLabel, setFolderLabel] = useState("");
  const [threads, setThreads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [counts, setCounts] = useState(() => cacheGet("counts") || {});
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadData, setThreadData] = useState(null);
  const [conns, setConns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catCounts, setCatCounts] = useState({});
  const [catFilter, setCatFilter] = useState("");
  const loadListRef = useRef(null);
  const [siReplying, setSiReplying] = useState({});

  useEffect(() => {
    if (!mailbox) { setSiReplying({}); return; }
    const loadP = () => api.get(`/shared-inboxes/${mailbox}/presence`).then((r) => setSiReplying(r.data.threads || {})).catch(() => {});
    loadP();
    const iv = setInterval(loadP, 10000);
    return () => clearInterval(iv);
  }, [mailbox]);

  useEffect(() => {
    const loadConns = () => api.get("/connections").then((r) => setConns(r.data)).catch(() => {});
    loadConns();
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.post("/emails/backfill-html").catch(() => {});
    const iv = setInterval(() => { loadListRef.current?.(); loadConns(); }, 60000);
    return () => clearInterval(iv);
  }, []);

  const loadList = useCallback(async () => {
    const key = `emails:${folder}:${search}:${page}:${catFilter}`;
    const hit = cacheGet(key);
    if (hit) { setThreads(hit.threads); setTotal(hit.total); }
    try {
      const params = { folder, search, page };
      if (mailbox) params.mailbox = mailbox;
      if (catFilter === "vip") params.vip = true;
      else if (catFilter) params.category_id = catFilter;
      const { data } = await api.get("/emails", { params });
      cacheSet(key, data);
      setThreads(data.threads); setTotal(data.total);
      return data.total;
    } catch (e) { toast.error(errDetail(e)); }
    finally {
      api.get("/emails/counts", { params: mailbox ? { mailbox } : {} }).then((r) => { cacheSet("counts", r.data); setCounts(r.data); }).catch(() => {});
      api.get("/categories/counts", { params: { folder } }).then((r) => setCatCounts(r.data)).catch(() => {});
    }
    return -1;
  }, [folder, search, page, catFilter, mailbox]);
  loadListRef.current = loadList;

  const handleRefresh = () => {
    if (conns.some((c) => c.provider === "gmail" && c.status === "active")) {
      api.post("/sync/now").catch(() => {});
      setTimeout(loadList, 3000);
    }
    loadList();
  };

  useEffect(() => {
    loadList().then((total) => {
      if (total === 0 && folder === "inbox" && !search) setTimeout(loadList, 700);
    });
  }, [loadList, folder, search]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "gmail") {
      toast.success("Gmail connected! Importing your mailbox — this takes a few seconds…");
      api.post("/sync/now").catch(() => {});
      window.history.replaceState({}, "", "/app");
      const t = setInterval(loadList, 5000);
      setTimeout(() => clearInterval(t), 30000);
      return () => clearInterval(t);
    }
    if (params.get("connect_error")) {
      toast.error(`Gmail connection failed: ${params.get("connect_error")}`);
      window.history.replaceState({}, "", "/app");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openThread = async (tid) => {
    if (folder === "drafts") {
      const th = threads.find((t) => t.thread_id === tid);
      if (th?.latest?.is_draft) { navigate(`/compose?draft_id=${th.latest.id}`); return; }
    }
    setSelectedThread(tid);
    try {
      const { data } = await api.get(`/threads/${tid}`, { params: mailbox ? { mailbox } : {} });
      setThreadData(data);
      loadList();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const tid = searchParams.get("thread");
    if (tid) { openThread(tid); setSearchParams({}, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAll = (reloadThread = false) => {
    loadList();
    if (reloadThread && selectedThread) {
      api.get(`/threads/${selectedThread}`, { params: mailbox ? { mailbox } : {} }).then((r) => setThreadData(r.data)).catch(() => {});
    }
  };

  const toggleStar = async (t) => {
    try {
      await api.post(`/emails/${t.latest.id}/action`, { action: t.starred ? "unstar" : "star" }, { params: mailbox ? { mailbox } : {} });
      loadList();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const askAgent = (text) => window.dispatchEvent(new CustomEvent("agent-ask", { detail: { text } }));

  const openSearchHit = async (hit) => {
    setMailbox(hit.mailbox || ""); setMailboxName(hit.mailboxName || "");
    setFolder(hit.folder); setFolderLabel(hit.folderLabel || "");
    setPage(1); setCatFilter(""); setShowAudit(false);
    setSelectedThread(hit.threadId);
    try {
      const { data } = await api.get(`/threads/${hit.threadId}`, { params: hit.mailbox ? { mailbox: hit.mailbox } : {} });
      setThreadData(data);
    } catch (e) { toast.error(errDetail(e)); }
  };

  useEffect(() => {
    const h = (e) => { if (e.detail?.threadId) openThread(e.detail.threadId); };
    window.addEventListener("open-thread", h);
    return () => window.removeEventListener("open-thread", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="h-screen flex flex-col overflow-hidden" data-testid="inbox-page">
      <BillingBanner />
      <div className="flex-1 flex overflow-hidden">
      <Sidebar counts={counts} activeFolder={folder} activeMailbox={mailbox} onCompose={() => navigate(mailbox ? `/compose?mailbox=${mailbox}` : "/compose")}
        onSelectFolder={(f, label, mb, mbName) => { setMailbox(mb || ""); setMailboxName(mbName || ""); setFolder(f); setFolderLabel(label || ""); setPage(1); setSelectedThread(null); setThreadData(null); setShowAudit(false); }} />
      <div className="flex-1 flex flex-col overflow-hidden">
      {mailbox && (
        <div data-testid="si-context-strip" className="flex items-center gap-2 px-4 py-1.5 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 shrink-0">
          <span className="font-semibold">📥 {mailboxName || t2("si.shared_ctx", "Shared inbox")}</span>
          <span>· {t2("si.shared_label", "shared with your team")}</span>
          <button data-testid="si-audit-open-btn" onClick={() => setShowAudit(true)}
            className="ml-auto font-semibold text-amber-900 underline hover:no-underline">{t2("si.audit_btn", "Audit")}</button>
        </div>
      )}
      <div className="flex-1 flex overflow-hidden">
      <EmailList folder={folder} folderLabel={folderLabel} threads={threads} total={total} page={page} setPage={setPage}
        search={search} setSearch={setSearch} onRefresh={handleRefresh}
        lastSynced={conns.find((c) => c.provider === "gmail")?.last_synced_at}
        selectedThread={selectedThread} onSelect={openThread} onToggleStar={toggleStar}
        wide={!threadData} categories={categories} catCounts={catCounts} catFilter={catFilter}
        replyingMap={siReplying}
        setCatFilter={(v) => { setCatFilter(v); setPage(1); }}
        globalSearch={<GlobalSearch onOpenHit={openSearchHit} />} />
      {threadData && (
        <ThreadView thread={threadData} userEmail={user?.email} categories={categories} mailbox={mailbox}
          onClose={() => { setSelectedThread(null); setThreadData(null); }}
          onChanged={refreshAll} onAskAgent={askAgent} />
      )}
      <AgentPanel threadId={selectedThread} refreshEmails={() => refreshAll(true)} mailbox={mailbox} />
      </div>
      </div>
      {showAudit && mailbox && <AuditPanel mailboxId={mailbox} mailboxName={mailboxName} onClose={() => setShowAudit(false)} />}
      </div>
    </div>
  );
}
